import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getLockerSpotLogs } from '../../../../../api/ticket';

// 자리 로그 행위 라벨·색 (#165). 백엔드 BranchLockerLogAction 과 1:1.
// 양도·양수도 자리 입장에서는 결국 배정 해제·배정이라 그렇게 부르고, 양도 상대방은 비고(사유)에 남는다(사용자 요청 2026-10-13).
const ACTION_META = {
  ASSIGN: { label: '배정', color: '#1F3A5F' },
  SALE_ASSIGN: { label: '판매 배정', color: '#1F3A5F' },
  EXTEND: { label: '연장', color: '#1F3A5F' },
  TRANSFER_IN: { label: '배정', color: '#1F3A5F' },
  MOVE_IN: { label: '이동해 옴', color: '#3B6FB6' },
  MOVE_OUT: { label: '자리 이동', color: '#3B6FB6' },
  UNASSIGN: { label: '배정 해제', color: '#737373' },
  TRANSFER_OUT: { label: '배정 해제', color: '#737373' },
  EXPIRE_UNLINK: { label: '만료 정리', color: '#737373' },
  REFUND: { label: '환불·판매취소', color: '#B0573E' },
  BACKFILL: { label: '과거 기록', color: '#999' },
};

// 구분 필터 — 자리 입장에서 묶는다(양수·이동해 옴·과거 기록 = 배정, 양도·이동·만료 정리 = 배정 해제)
const ACTION_GROUPS = [
  { key: 'ALL', label: '전체', actions: null },
  { key: 'IN', label: '배정', actions: ['ASSIGN', 'SALE_ASSIGN', 'EXTEND', 'TRANSFER_IN', 'MOVE_IN', 'BACKFILL'] },
  { key: 'OUT', label: '배정 해제', actions: ['UNASSIGN', 'TRANSFER_OUT', 'MOVE_OUT', 'EXPIRE_UNLINK'] },
  { key: 'REFUND', label: '환불·판매취소', actions: ['REFUND'] },
];

// 기간 필터는 그 줄의 기간(없으면 처리일)이 선택 범위와 겹치면 남긴다. yyyy-MM-dd 문자열 비교.
const rowRange = log => {
  const start = log.usageStartDate ?? log.registeredDate?.slice(0, 10);
  return [start, log.usageEndDate ?? start];
};

const PAGE_SIZE = 10;

const filterSx = {
  '& .MuiInputBase-root': { height: 34, fontSize: 13 },
  '& .MuiInputBase-input': { py: 0, px: '10px' },
};

const head = {
  fontSize: 12.5,
  fontWeight: 600,
  color: '#595959',
  background: '#F5F7FA',
  py: '10px',
  whiteSpace: 'nowrap',
};
const cell = { fontSize: 13, color: '#262626', py: '10px', borderBottom: '1px solid #F0F0F0' };

const Badge = ({ meta, t }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      minWidth: 72,
      fontSize: 11.5,
      fontWeight: 600,
      color: meta.color,
      border: `1px solid ${meta.color}`,
      borderRadius: '999px',
      p: '2px 8px',
      whiteSpace: 'nowrap',
    }}>
    {t(meta.label)}
  </Box>
);

/**
 * 라커관리 자리 상세의 '이력' 탭 (#165) — 처리 한 건이 한 줄(최신순).
 * 기간 = 그 줄이 열거나 닫은 구간(누가 언제부터 언제까지 이 자리를 갖고 있었는지). 양도·취소도 그대로 남는다.
 * 필터 = 구분(배정/배정 해제/환불) · 회원 · 기간(그 줄의 기간이 범위와 겹치면 표시). 화면 안에서만 거르고 10건씩 나눠 보여준다(자리당 이력이 많지 않음).
 * 과거 기록(BACKFILL)은 로그 도입 전 데이터를 패스 기준으로 역산한 것이다(시작 = 패스 생성일).
 */
const LockerSpotLogTab = ({ lockerId }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const enabled = !!lockerId && !!user?.token;

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['lockerSpotLogs', lockerId],
    queryFn: () => getLockerSpotLogs(lockerId, user.token),
    enabled,
    staleTime: 0,
  });

  const [group, setGroup] = useState('ALL');
  const [memberId, setMemberId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  // 회원 필터 후보 — 이력에 나온 회원(동명이인 구분을 위해 전화번호 함께)
  const members = useMemo(() => {
    const byId = new Map();
    logs.forEach(log => {
      if (log.memberId && !byId.has(log.memberId)) {
        byId.set(log.memberId, `${log.memberName ?? t('알 수 없음')} (${log.memberPhoneNumber ?? '-'})`);
      }
    });
    return [...byId.entries()];
  }, [logs, t]);

  const filtered = useMemo(() => {
    const actions = ACTION_GROUPS.find(g => g.key === group)?.actions;
    return logs.filter(log => {
      if (actions && !actions.includes(log.action)) return false;
      if (memberId && log.memberId !== memberId) return false;
      const [start, end] = rowRange(log);
      if (from && end && end < from) return false;
      if (to && start && start > to) return false;
      return true;
    });
  }, [logs, group, memberId, from, to]);

  // 필터가 바뀌면 첫 페이지로
  useEffect(() => setPage(1), [group, memberId, from, to]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setGroup('ALL');
    setMemberId('');
    setFrom('');
    setTo('');
  };

  if (logsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (logs.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: '#999', textAlign: 'center', py: 6 }}>
        {t('이 자리의 사용 이력이 없습니다')}
      </Typography>
    );
  }

  return (
    <>
      {/* 필터 — 구분 / 회원 / 기간 */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {ACTION_GROUPS.map(g => (
          <Button
            key={g.key}
            size="small"
            onClick={() => setGroup(g.key)}
            sx={{
              height: 30,
              px: '12px',
              borderRadius: '999px',
              fontSize: 12.5,
              fontWeight: group === g.key ? 700 : 500,
              color: group === g.key ? '#fff' : '#404040',
              backgroundColor: group === g.key ? '#1F3A5F' : '#fff',
              border: `1px solid ${group === g.key ? '#1F3A5F' : '#D9D9D9'}`,
              '&:hover': { backgroundColor: group === g.key ? '#172C48' : '#F5F7FA' },
            }}>
            {t(g.label)}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={memberId}
          onChange={e => setMemberId(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          sx={{ ...filterSx, minWidth: 160 }}>
          <MenuItem value="">{t('전체 회원')}</MenuItem>
          {members.map(([id, label]) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} sx={filterSx} />
        <Typography sx={{ fontSize: 13, color: '#8C8C8C' }}>~</Typography>
        <TextField type="date" size="small" value={to} onChange={e => setTo(e.target.value)} sx={filterSx} />
        <Button size="small" onClick={resetFilters} sx={{ height: 30, fontSize: 12.5, color: '#595959' }}>
          {t('초기화')}
        </Button>
      </Box>
      <Typography sx={{ fontSize: 12, color: '#8C8C8C', mt: 1 }}>
        {t('총')} {filtered.length}
        {t('건')}
      </Typography>

      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...head, width: 110 }} align="center">
              {t('구분')}
            </TableCell>
            <TableCell align="center" sx={{ ...head, width: 90 }}>
              {t('회원명')}
            </TableCell>
            <TableCell align="center" sx={{ ...head, width: 120 }}>
              {t('전화번호')}
            </TableCell>
            <TableCell align="center" sx={{ ...head, width: 200 }}>
              {t('사용기간')}
            </TableCell>
            <TableCell align="center" sx={head}>
              {t('비고')}
            </TableCell>
            <TableCell sx={{ ...head, width: 90 }} align="center">
              {t('처리일')}
            </TableCell>
            <TableCell sx={{ ...head, width: 80 }} align="center">
              {t('처리직원')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell align="center" colSpan={7} sx={{ ...cell, color: '#999' }}>
                {t('조건에 맞는 이력이 없습니다')}
              </TableCell>
            </TableRow>
          )}
          {paged.map(log => (
            <TableRow key={log.id} hover>
              <TableCell sx={cell} align="center">
                <Badge meta={ACTION_META[log.action] ?? { label: log.action, color: '#737373' }} t={t} />
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                {log.memberName ?? t('알 수 없음')}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, color: '#595959', whiteSpace: 'nowrap' }}>
                {log.memberPhoneNumber ?? '-'}
              </TableCell>
              {/* 이 줄이 열거나 닫은 구간 — 누가 언제부터 언제까지 이 자리를 갖고 있었는지 */}
              <TableCell align="center" sx={{ ...cell, fontWeight: 600, color: '#1F3A5F', whiteSpace: 'nowrap' }}>
                {log.usageStartDate ? `${log.usageStartDate} ~ ${log.usageEndDate}` : '-'}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontSize: 12, color: '#8C8C8C' }}>
                {log.reason ?? '-'}
              </TableCell>
              <TableCell sx={{ ...cell, fontSize: 12, color: '#8C8C8C', whiteSpace: 'nowrap' }} align="center">
                {log.registeredDate?.slice(0, 10)}
              </TableCell>
              <TableCell sx={{ ...cell, fontSize: 12, color: '#8C8C8C', whiteSpace: 'nowrap' }} align="center">
                {log.registeredByName ?? (log.source === 'BACKFILL' ? '-' : t('자동'))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            shape="rounded"
            size="small"
            sx={{
              '& .MuiPaginationItem-root': { fontSize: 12.5 },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#1F3A5F',
                color: '#fff',
                '&:hover': { backgroundColor: '#172C48' },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default LockerSpotLogTab;
